import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, Check, DollarSign } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

const CommunicationSuiteComparisonSlide = () => {
  const analysisData = [
    {
      function: "VoIP Phone System",
      alternatives: "Weave, Mango Voice",
      limitations: "Not AI-connected. No real-time transcription, no ability for AI agents to listen/transfer, and call data is siloed."
    },
    {
      function: "2-Way Messaging",
      alternatives: "RevenueWell, Podium",
      limitations: "Requires staff to manually type every response. No AI agents drafting replies or handling scheduling conversations."
    },
    {
      function: "Digital Forms",
      alternatives: "Yapi, Modento",
      limitations: "Static forms. Data often doesn't automatically write back into specific PMS fields, requiring manual entry."
    },
    {
      function: "Email Marketing",
      alternatives: "Mailchimp, Constant Contact",
      limitations: "Generic templates. Not linked to clinical data, so you can't segment by specific treatment needs or insurance status."
    },
    {
      function: "Review Generation",
      alternatives: "Swell, Birdeye",
      limitations: "Just sends a generic link. Asha's AI ensures the patient had a good experience before asking."
    },
    {
      function: "Integrated Payments",
      alternatives: "Weave, Text2Pay",
      limitations: "Just a payment link. Asha's AI calls/texts patients to explain why they owe the balance, increasing collections."
    },
    {
      function: "Mass SMS Campaigns",
      alternatives: "RevenueWell, SolutionReach",
      limitations: "One-way blasts. If a patient replies, your front desk gets overwhelmed. Asha's AI handles all replies automatically."
    },
    {
      function: "Online Scheduling",
      alternatives: "LocalMed, Zocdoc",
      limitations: "Separate portal that lives outside your core workflow. Asha's scheduler reads live availability in Asha PMS and auto-confirms with smart reminders."
    }
  ];

  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 lg:p-10 font-sans overflow-hidden">
      
      {/* Background Decor */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/50 rounded-full blur-[100px] opacity-40 -translate-y-1/2 translate-x-1/4 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-indigo-50/50 rounded-full blur-[100px] opacity-40 translate-y-1/2 -translate-x-1/4 pointer-events-none" />

      {/* Header with Price Card */}
      <div className="flex flex-col lg:flex-row justify-between items-start mb-6 flex-shrink-0 gap-6 relative z-10">
        <div className="max-w-3xl">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-3 tracking-tight leading-tight">
            <span className="text-blue-600">Communication & Marketing:</span> How much do other tools cost?
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed max-w-2xl">
            Consolidating your communication stack saves money and headaches.
            <span className="font-bold text-black ml-1">
               Other tools bundle these features but lack the deep AI integration that drives actual production.
            </span>
          </p>
        </div>

        {/* Price Card */}
        <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-lg border border-red-100 p-5 flex flex-col items-start gap-1 min-w-[280px]"
        >
            <div className="flex items-center gap-2 text-slate-500 font-bold text-xs uppercase tracking-wider mb-1">
                <div className="p-1.5 bg-red-100 rounded-lg">
                    <DollarSign className="w-3.5 h-3.5 text-red-600" />
                </div>
                Competitor Cost
            </div>
            <div className="text-3xl font-extrabold text-red-500">
                $750 - $1,200
            </div>
            <div className="text-sm font-medium text-slate-400">
                per month
            </div>
        </motion.div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col min-h-0 relative z-10">
        <div className="flex-1 overflow-auto relative custom-scrollbar">
          <Table>
            <TableHeader className="bg-slate-50 sticky top-0 z-10 shadow-sm">
              <TableRow className="hover:bg-slate-50 border-b-slate-200">
                <TableHead className="w-[25%] text-slate-900 font-bold text-base py-4 pl-6">Feature</TableHead>
                <TableHead className="w-[30%] text-slate-900 font-bold text-base py-4">Other Tools</TableHead>
                <TableHead className="w-[45%] text-slate-900 font-bold text-base py-4 pr-6">Key Limitations</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {analysisData.map((item, idx) => (
                <TableRow 
                    key={idx} 
                    className="transition-colors border-b border-slate-50 last:border-0 hover:bg-blue-50/30"
                >
                  <TableCell className="pl-6 align-middle font-bold text-slate-800 text-sm md:text-base py-4">
                    {item.function}
                  </TableCell>
                  
                  {/* Alternatives Column */}
                  <TableCell className="align-middle text-slate-600 text-sm md:text-base py-4 font-medium">
                    {item.alternatives}
                  </TableCell>

                  {/* Limitations Column */}
                  <TableCell className="align-middle pr-6 text-slate-500 text-sm md:text-sm py-4 leading-relaxed">
                     <div className="flex gap-2">
                        <AlertCircle className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                        <span>{item.limitations}</span>
                     </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default CommunicationSuiteComparisonSlide;
